import Sheet from '@/structs/sheet.js'
import Transaction from '@/structs/transaction.js'
import Rule from '@/structs/rule.js'
import BankTransactions from '@/adapters/processors/bank-transactions.js'
import { Filter } from '@/structs/filter.js'
import { getStartOfMonth, getEndOfYear } from '@/parse-and-play/utils.js'

const API_URL = 'https://fastfinance-api.dallasread.com'

const encrypt = async (data, keyHex) => {
  const encoder = new TextEncoder()
  const keyBytes = new Uint8Array(keyHex.match(/.{2}/g).map(byte => parseInt(byte, 16)))
  const key = await window.crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  )

  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(JSON.stringify(data))
  )

  const encryptedArray = new Uint8Array(encrypted)
  const authTag = encryptedArray.slice(-16)
  const ciphertext = encryptedArray.slice(0, -16)

  const payload = {
    iv: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
    authTag: Array.from(authTag).map(b => b.toString(16).padStart(2, '0')).join(''),
    encrypted: Array.from(ciphertext).map(b => b.toString(16).padStart(2, '0')).join('')
  }

  return btoa(JSON.stringify(payload))
}

class Commands {
  constructor (options) {
    for (const key in options) {
      this[key] = options[key]
    }
  }

  restoreIdentitiesFromLocal () {
    return new Promise((resolve, reject) => {
      this.identitiesAdapter.restore()
        .then((identities) => {
          identities.forEach((identity) => {
            ;(identity.sources || []).forEach((source) => {
              source.identityId = identity.id
            })

            identity.rules = (identity.rules || []).map((rule) => {
              rule.identityId = identity.id
              return new Rule(rule)
            })

            identity.stagedTransactions = (identity.stagedTransactions || []).map((st) => {
              st.identityId = identity.id
              st.isStaged = true
              return new Transaction(st)
            })

            identity.sheets.forEach((sheet) => {
              sheet.identityId = identity.id
              sheet.dateFilter = Filter.fromObj(sheet.dateFilter)
              sheet.typeFilter = Filter.fromObj(sheet.typeFilter)
              sheet.filters = sheet.filters
                .filter((filter) => filter)
                .map((filter) => Filter.fromObj(filter))
            })

            this.state.add('identities', [identity])
            this.state.add('sources', identity.sources || [])
            this.state.add('rules', identity.rules || [])
            this.state.add('transactions', identity.stagedTransactions || [])
            this.state.add('sheets', identity.sheets || [])
          })

          resolve()
        })
        .catch(reject)
    })
  }

  addSeedIdentityIfRequired () {
    if (!this.queries.findAllIdentities().length) {
      this.addIdentity({ name: 'Dunder Mifflin' })

      const identity = this.queries.findAllIdentities()[0]
      this.addSourceForIdentity(identity, { type: 'DunderMifflin', processor: 'PlainText' })
    }
  }

  addSeedSheetIfRequired (identity) {
    if (!this.queries.findAllSheets(identity.id).length) {
      Sheet.SHEETS.forEach((sheet) => {
        this.addSheetForIdentity(identity, sheet)
      })
    }
  }

  addIdentity (data) {
    const identity = this.state.add('identities', [data])[0]

    this.addSeedSheetIfRequired(identity)
    this._saveIdentity(identity)

    return identity
  }

  updateIdentityName (identity, name) {
    identity.name = name
    this._saveIdentity(identity)
  }

  updateIdentitySettings (identity, settings) {
    for (const key in settings) {
      identity[key] = settings[key]
    }
    this._saveIdentity(identity)
  }

  addRuleForIdentity (identity, data) {
    data.identityId = identity.id
    const rule = this.state.add('rules', [new Rule(data)])[0]
    this.recategorizeStagedTransactions(identity)
    this._saveIdentity(identity)
    return rule
  }

  saveRuleForIdentity (identity, rule, data) {
    Object.assign(rule, data)
    this.recategorizeStagedTransactions(identity)
    this._saveIdentity(identity)
  }

  removeRuleForIdentity (identity, rule) {
    this.state.removeAll('rules', [rule])
    this.recategorizeStagedTransactions(identity)
    this._saveIdentity(identity)
  }

  recategorizeStagedTransactions (identity) {
    const staged = this.queries.findAllStagedTransactions(identity.id)
    const rules = this.queries.findAllRules(identity.id)
    const sources = this.queries.findAllSources(identity.id)

    for (const t of staged) {
      if (!t.raw) continue // Skip if no original data
      if (t.edited) continue // Skip if user has edited

      const source = sources.find(s => s.id === t.sourceId)
      const applicableRules = rules.filter(r => r.appliesTo(t.sourceId))
      const elidedAccount = source?.config?.elidedAccount || 'Assets:Unknown'

      // Always start from original data
      const originalPayee = t.raw.payee || t.payee
      let category = 'Expenses:Uncategorized'
      let payee = originalPayee
      const str = JSON.stringify(t.raw)

      for (const rule of applicableRules) {
        if (!rule.pattern) continue
        try {
          const regex = new RegExp(rule.pattern, 'i')
          if (regex.test(str)) {
            if (rule.payee) payee = rule.payee
            if (rule.category) category = rule.category
            break
          }
        } catch (e) { /* invalid regex */ }
      }

      // Update transaction
      t.payee = payee
      t.category = category
      if (t.postings && t.postings.length >= 2) {
        t.postings[0].account = category
        t.postings[1].account = elidedAccount
      }
    }
  }

  addStagedTransactionsForIdentity (identity, transactions) {
    const existingStagedHashes = new Set(
      this.queries.findAllStagedTransactions(identity.id).map(t => t.hash)
    )

    const committedComparisonHashes = new Set(
      this.queries.findAllCommittedTransactions(identity.id).map(t => Transaction.computeComparisonHash(t))
    )

    const newTransactions = transactions
      .map(t => {
        const hash = Transaction.computeHash(t)
        const comparisonHash = Transaction.computeComparisonHash(t)
        const isDuplicateOfCommitted = committedComparisonHashes.has(comparisonHash)

        return new Transaction({
          identityId: identity.id,
          isStaged: true,
          status: isDuplicateOfCommitted ? 'ignored' : 'pending',
          hash: hash,
          date: t.date,
          payee: t.payee,
          amount: t.amount,
          category: t.category,
          postings: t.postings,
          raw: t.raw,
          sourceId: t.sourceId
        })
      })
      .filter(t => !existingStagedHashes.has(t.hash))

    const added = this.state.add('transactions', newTransactions)
    this._saveIdentity(identity)
    return added
  }

  saveStagedTransactionForIdentity (identity, staged, data) {
    Object.assign(staged, data)
    this._saveIdentity(identity)
  }

  removeStagedTransactionForIdentity (identity, staged) {
    this.state.removeAll('transactions', [staged])
    this._saveIdentity(identity)
  }

  clearStagedTransactionsForIdentity (identity) {
    const all = this.queries.findAllStagedTransactions(identity.id)
    this.state.removeAll('transactions', all)
    this._saveIdentity(identity)
  }

  _saveIdentity (identity) {
    const data = {
      id: identity.id,
      name: identity.name,
      apiToken: identity.apiToken,
      defaultCurrency: identity.defaultCurrency,
      selectedSheetId: identity.selectedSheetId,
      sources: this.queries.findAllSources(identity.id).map((source) => {
        return {
          id: source.id,
          type: source.type,
          processor: source.processor,
          config: JSON.parse(JSON.stringify(source.config || {})),
          raw: source.raw,
          updatedAt: source.updatedAt,
          lastSyncedAt: source.lastSyncedAt
        }
      }),
      rules: this.queries.findAllRules(identity.id).map((rule) => {
        return {
          id: rule.id,
          pattern: rule.pattern,
          payee: rule.payee,
          category: rule.category,
          sourceIds: JSON.parse(JSON.stringify(rule.sourceIds || []))
        }
      }),
      stagedTransactions: this.queries.findAllStagedTransactions(identity.id).map((t) => {
        return {
          id: t.id,
          sourceId: t.sourceId,
          date: t.date,
          payee: t.payee,
          amount: t.amount,
          category: t.category,
          postings: JSON.parse(JSON.stringify(t.postings || [])),
          raw: JSON.parse(JSON.stringify(t.raw || {})),
          status: t.status || 'pending',
          hash: t.hash,
          edited: t.edited || false
        }
      }),
      sheets: this.queries.findAllSheets(identity.id).map((sheet) => {
        return {
          id: sheet.id,
          name: sheet.name,
          ordinal: sheet.ordinal,
          typeFilter: JSON.parse(JSON.stringify(sheet.typeFilter)),
          dateFilter: JSON.parse(JSON.stringify(sheet.dateFilter)),
          filters: JSON.parse(JSON.stringify(sheet.filters || []))
        }
      })
    }

    this.identitiesAdapter.set(identity.id, data)
  }

  removeIdentity (identity) {
    this.identitiesAdapter.remove(identity.id)
    this.state.removeAll('identities', [identity])
  }

  resetTransactionsForIdentity (identity) {
    // Only remove non-staged transactions - preserve staged ones
    const transactions = this.queries.findAllTransactions(identity.id)
      .filter(t => !t.isStaged)

    this.removeTransactions(transactions)
  }

  addTransactionsForIdentity (identity, transactions) {
    transactions.forEach((transaction) => {
      transaction.identityId = identity.id
    })

    this.state.add('transactions', transactions)
  }

  removeTransactions (transactions) {
    this.state.removeAll('transactions', transactions)
  }

  addPeriodicTransactionsForIdentity (identity, periodics) {
    periodics.forEach((periodic) => {
      periodic.identityId = identity.id
    })

    this.state.add('periodics', periodics)
  }

  rebuildPeriodicTransactionsForFilters (identity, dateFilter) {
    const existing = this.queries.findAllPeriodicTransactions()
    this.state.removeAll('transactions', existing)
  }

  resetAccountsForIdentity (identity) {
    const accounts = this.queries.findAllAccounts(identity.id)

    this.state.removeAll('accounts', accounts)
  }

  addAccountsForIdentity (identity, accounts) {
    accounts.forEach((account) => {
      account.identityId = identity.id
    })

    this.state.add('accounts', accounts)
  }

  addSourceForIdentity (identity, data) {
    data.identityId = identity.id

    const source = this.state.add('sources', [data])[0]

    this._saveIdentity(identity)

    return source
  }

  saveSourceForIdentity (identity, source, data) {
    Object.assign(source, data)
    source.updatedAt = Date.now()
    this._saveIdentity(identity)
  }

  removeSourceForIdentity (identity, source) {
    this.state.removeAll('sources', [source])
    this._saveIdentity(identity)
  }

  addSheetForIdentity (identity, data) {
    data.identityId = identity.id

    const sheet = this.state.add('sheets', [data])[0]

    this._saveIdentity(identity)

    return sheet
  }

  saveSheetForIdentity (identity, sheet, data) {
    Object.assign(sheet, data)
    sheet.updatedAt = Date.now()
    this._saveIdentity(identity)
  }

  removeSheetForIdentity (identity, sheet) {
    this.state.removeAll('sheets', [sheet])
    this._saveIdentity(identity)
  }

  saveSheetOrderForIdentity (identity, sheetIds) {
    for (var i = 0; i < sheetIds.length; i++) {
      const sheet = this.queries.findSheetForIdentity(identity.id, sheetIds[i])

      if (sheet) {
        sheet.ordinal = i
      }
    }

    this._saveIdentity(identity)
  }

  async fetchSource (identity, source) {
    const adapter = this.queries.sourceFetchAdapter(source)

    return await adapter.fetch()
  }

  async processSourceContent (identity, source, content) {
    const adapter = this.queries.sourceProcessorAdapter(source)

    return await adapter.process(content)
  }

  // Shared by a live fetch and a cache-only hydration: turn already-fetched
  // content into the transactions/accounts/periodics a source produces,
  // routing into staging if the source is configured for it.
  async _applySourceContent (identity, source, content) {
    const data = await this.processSourceContent(identity, source, content)

    if (source.config?.staging && data.transactions?.length) {
      const transactions = data.transactions.map(t => ({
        ...t,
        sourceId: source.id,
        raw: { payee: t.payee, date: t.date, amount: t.amount }
      }))
      this.addStagedTransactionsForIdentity(identity, transactions)
      return { ...data, transactions: [] }
    }

    return data
  }

  async refreshIdentitySource (identity, source) {
    // Bank accounts sync manually only
    if (source.type === 'BankAccount' || source.type === 'CustomAPI') {
      return { transactions: [], accounts: [], periodics: [] }
    }

    try {
      source.isLoading = true

      source.raw = await this.fetchSource(identity, source)
      const data = await this._applySourceContent(identity, source, source.raw)

      source.isLoading = false

      return data
    } catch (e) {
      source.isLoading = false
      throw e
    }
  }

  // Renders from whatever content was fetched last time (persisted as
  // source.raw), with no network call -- lets the app show something
  // immediately on load instead of blocking on a fresh git clone every
  // single time, while refreshIdentitySources still runs for real data.
  async hydrateIdentitySourceFromCache (identity, source) {
    if (source.type === 'BankAccount' || source.type === 'CustomAPI' || !source.raw) {
      return { transactions: [], accounts: [], periodics: [] }
    }

    try {
      return await this._applySourceContent(identity, source, source.raw)
    } catch (e) {
      // A cache-only hydration failing (e.g. the processor changed) isn't
      // fatal -- the real fetch that follows will still surface the error.
      return { transactions: [], accounts: [], periodics: [] }
    }
  }

  async hydrateIdentitySourcesFromCache (identity) {
    const sources = this.queries.findAllSources(identity.id)
    const results = []

    for (const source of sources) {
      results.push(await this.hydrateIdentitySourceFromCache(identity, source))
    }

    return results
  }

  async syncBankSource (identity, source, force = false) {
    if (!force && !source.needsSync) {
      return { transactions: [], accounts: [], periodics: [], skipped: true }
    }

    const apiUrl = source.type === 'CustomAPI' ? source.config?.apiUrl : API_URL

    const apiToken = identity.apiToken
    if (!apiToken && source.type !== 'CustomAPI') {
      throw new Error('Missing API token. Set it in Settings.')
    }

    const { accountType, username, password, firstCarAnswer, oldestCousinAnswer, fatherCityAnswer } = source.config || {}
    if (!username || !password) {
      throw new Error(`Missing credentials for ${this.queries.getSourceName(source)}`)
    }

    source.isLoading = true

    try {
      const handshakeResponse = await fetch(`${apiUrl}/handshake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiToken}`
        }
      })

      if (!handshakeResponse.ok) {
        const error = await handshakeResponse.json()
        throw new Error(error.error || 'Handshake failed')
      }

      const { sessionToken, encryptionKey } = await handshakeResponse.json()

      const encryptedPayload = await encrypt({
        username,
        password,
        securityAnswers: { firstCarAnswer, oldestCousinAnswer, fatherCityAnswer }
      }, encryptionKey)

      const response = await fetch(`${apiUrl}/fetch/${accountType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`
        },
        body: JSON.stringify({ encrypted: encryptedPayload })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'API error')
      }

      const { jobId } = await response.json()

      // Poll for job completion
      let job
      while (true) {
        const jobResponse = await fetch(`${apiUrl}/job/${jobId}`)

        if (!jobResponse.ok) {
          throw new Error('Job lost - server may have restarted. Please try again.')
        }

        job = await jobResponse.json()

        if (job.status === 'complete') break
        if (job.status === 'error') throw new Error(job.error || 'Fetch failed')

        await new Promise(resolve => setTimeout(resolve, 2000))
      }

      // Handle both array and object response formats
      const result = job.result || []
      const rawTransactions = Array.isArray(result) ? result : (result.transactions || [])
      if (result.balance !== undefined) {
        source.config = source.config || {}
        source.config.reportedBalance = result.balance
      }

      // Get rules that apply to this source
      const rules = this.queries.findAllRules(identity.id)
        .filter(r => r.appliesTo(source.id))

      // Process through BankTransactions to create proper postings
      const elidedAccount = source.config?.elidedAccount || 'Assets:Unknown'
      const processor = new BankTransactions({ rules, elidedAccount })
      const { transactions } = await processor.process(JSON.stringify(rawTransactions))

      transactions.forEach(t => { t.sourceId = source.id })

      source.lastSyncedAt = Date.now()
      source.isLoading = false

      // If staging is enabled (default for BankAccount), add to staging
      if (source.config?.staging !== false) {
        this.addStagedTransactionsForIdentity(identity, transactions)
        this._saveIdentity(identity)
        return { transactions: [], accounts: [], periodics: [] }
      }

      // Otherwise return transactions to be added directly
      this._saveIdentity(identity)
      return { transactions, accounts: [], periodics: [] }
    } catch (e) {
      source.isLoading = false
      throw e
    }
  }

  async refreshIdentitySources (identity) {
    const sources = this.queries.findAllSources(identity.id)
    const results = []

    for (const source of sources) {
      try {
        const result = await this.refreshIdentitySource(identity, source)
        source.error = null
        results.push(result)
      } catch (e) {
        // Store error on source but continue with others
        source.error = e.message
        console.error(`Failed to refresh source ${source.id}:`, e.message)
        results.push({ transactions: [], accounts: [], periodics: [] })
      }
    }

    return results
  }

  regeneratePeriodicTransactions (identity, dateFilter) {
    const periodicTransactions = this.queries.findAllPeriodicTransactions(identity.id)
    this.removeTransactions(periodicTransactions)

    if (!dateFilter.startAt || !dateFilter.endAt) {
      const twoYearsFromNow = new Date(new Date().getFullYear() + 1, 0, 1)
      dateFilter = { startAt: getStartOfMonth(new Date()), endAt: getEndOfYear(twoYearsFromNow) }
    }

    this.generatePeriodicTransactions(identity, dateFilter)
  }

  generatePeriodicTransactions (identity, dateFilter) {
    const periodics = this.queries.findAllPeriodics(identity.id)
    const allPeriodicTransactions = periodics.reduce((acc, periodic) => {
      const transactions = periodic.transactionsFor(dateFilter.startAt, dateFilter.endAt)
      transactions.forEach((t) => { t.periodicName = periodic.name })
      return acc.concat(transactions)
    }, [])

    allPeriodicTransactions.forEach((periodicTransaction) => {
      const existingPostings = this.queries
        .findAllTransactionsBetween(identity.id, periodicTransaction.periodicRange.startAt, periodicTransaction.periodicRange.endAt)
        .reduce((acc, t) => acc.concat(t.getPostings()), [])

      periodicTransaction.postings.forEach((periodicPosting) => {
        const amount = periodicPosting.amount
        const existingSum = existingPostings
          .filter((posting) => !posting.periodicName)
          .filter((posting) => periodicPosting.currency === posting.currency)
          .filter((posting) => periodicPosting.account === posting.account)
          .reduce((sum, posting) => {
            return sum + posting.amount
          }, 0)

        if (Math.abs(amount) > Math.abs(existingSum)) {
          periodicPosting.amount = amount - existingSum
        } else {
          periodicPosting.amount = 0
        }
      })
    })

    this.addTransactionsForIdentity(identity, allPeriodicTransactions)
  }

  reset () {
    this.state.reset()
  }

  download (identity) {
    const content = this.queries.identityToPlainText(identity)
    const blob = this.blob([content], { type: 'text/plain;charset=utf-8' })

    this.saveAs(blob, `${identity.id}.txt`)
  }
}

export default Commands
